/**
 * ClassContext.js
 * Context for managing class-related state globally
 *
 * Usage:
 * const { currentClass, students, selectClass } = useClassContext();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { subscribeToClasses } from '../services/firebase/classService';
import { getUsersByIds } from '../services/firebase/userService';
import { getRoles } from '../utils/roles';
import { useAuth } from './UserContext';

const ClassContext = createContext(null);

export const useClassContext = () => {
  const context = useContext(ClassContext);
  if (!context) {
    throw new Error('useClassContext must be used within ClassProvider');
  }
  return context;
};

export const ClassProvider = ({ children }) => {
  const { allClasses, allUsers, userData } = useAuth();

  // Current selected class
  const [currentClassId, setCurrentClassId] = useState(null);

  // Derived state
  const currentClass = useMemo(() =>
    allClasses?.find(c => c.key === currentClassId) || null,
    [allClasses, currentClassId]
  );

  // Get students in current class
  const students = useMemo(() => {
    if (!currentClass?.studentIds || !allUsers) return [];
    const studentIds = currentClass.studentIds;
    return allUsers.filter(u =>
      studentIds.includes(u.key) &&
      getRoles(u).includes('student')
    );
  }, [currentClass, allUsers]);

  // Get teachers in current class
  const teachers = useMemo(() => {
    if (!currentClass?.teacherIds || !allUsers) return [];
    const teacherIds = currentClass.teacherIds;
    return allUsers.filter(u =>
      teacherIds.includes(u.key) &&
      getRoles(u).includes('teacher')
    );
  }, [currentClass, allUsers]);

  // Select a class
  const selectClass = useCallback((classId) => {
    setCurrentClassId(classId);
  }, []);

  // Clear selected class
  const clearClass = useCallback(() => {
    setCurrentClassId(null);
  }, []);

  // Get classes for current user
  const myClasses = useMemo(() => {
    if (!userData?.firestore || !allClasses) return [];

    const roles = getRoles(userData.firestore);
    const userId = userData.auth?.uid;

    if (roles.includes('admin')) {
      return allClasses;
    }

    if (roles.includes('teacher')) {
      return allClasses.filter(c =>
        c.teacherId === userId ||
        c.teacherIds?.includes(userId)
      );
    }

    if (roles.includes('student')) {
      return allClasses.filter(c =>
        c.studentIds?.includes(userId)
      );
    }

    return [];
  }, [userData, allClasses]);

  const value = {
    // Data
    allClasses,
    myClasses,
    currentClass,
    currentClassId,
    students,
    teachers,

    // Actions
    selectClass,
    clearClass,
  };

  return (
    <ClassContext.Provider value={value}>
      {children}
    </ClassContext.Provider>
  );
};

export default ClassContext;
